import type {ThemeOptions} from '@mui/material';
import {
  amber,
  dimBlue,
  glassBlack,
  glassPrimary,
  glassWhite,
  green,
  neutral,
  primary as newPrimary,
  red,
  yellow,
  nero
} from './colors';

const customColors = {
  // Text Colors
  text1: neutral[200],
  text2: glassBlack[60],
  text3: glassBlack[40],
  text4: glassBlack[20],

  onNegativeText: neutral.white,
  onNegativeText2: glassWhite[60],

  primaryBrandText: newPrimary[500],
  primaryBrandText2: newPrimary[650],

  onColorText: neutral.white,
  onColorText2: glassWhite[80],
  onColorText3: glassWhite[60],

  onColorDarkText: neutral[200],
  onColorDarkText2: glassBlack[60],
  onColorDarkText3: glassWhite[60],

  approveText: green[400],

  warningText: amber[300],
  warningText2: amber[500],

  criticalText: red[500],
  criticalText2: red[700],

  gradGoldenDarkSolid: amber[400],
  gradGoldenSolid: yellow[500],
  gradGoldenLightSolid: yellow[700],

  // Background Colors
  bg: neutral.white,
  bg2: dimBlue[990],
  bg3: nero[975],
  bg4: dimBlue[900],

  negativeBg: dimBlue[200],

  primaryBrandBg: newPrimary[500],
  primaryBrandBg2: newPrimary[600],
  primaryBrandBg3: newPrimary[800],
  primaryBrandBg4: newPrimary[950],
  primaryBrandBg5: newPrimary[975],
  primaryBrandBg6: newPrimary[400],

  primaryBrandBgGlass: glassPrimary[5],
  primaryBrandBgGlass2: glassPrimary[10],

  approveBg: green[500],
  approveBg2: green[600],
  approveBg3: green[950],
  approveBg4: green[975],

  warningBg: yellow[500],
  warningBg2: yellow[900],

  criticalBg: red[500],
  criticalBg2: red[600],
  criticalBg3: red[950],
  criticalBg4: red[975],
  criticalBg5: red[990],

  glassBg: glassBlack[2],
  glassBg2: glassBlack[5],
  glassOnColorBg: glassWhite[10],

  gradShell: (degree = 180) =>
    `linear-gradient(${degree}deg, ${neutral.white} 0%, ${dimBlue[950]} 100%)`,
  gradPrimaryBrand: (degree = 180) =>
    `linear-gradient(${degree}deg, ${newPrimary[500]} 0%, ${newPrimary[300]} 100%)`,
  gradGolden: (degree = 180) =>
    `linear-gradient(${degree}deg, ${amber[400]} 0%, ${yellow[500]} 50%, ${yellow[700]} 100%)`,

  smoke: glassBlack[40],
  hover: glassBlack[5],
  press: glassBlack[10],

  // Stroke Colors
  stroke: glassBlack[2],
  stroke2: glassBlack[5],
  stroke3: glassBlack[10],
  stroke4: glassBlack[20],
  stroke5: glassBlack[60],
  primaryBrandStroke: newPrimary[500],
  primaryBrandStroke2: newPrimary[700],
  primaryBrandStroke3: newPrimary[900],
  primaryBrandStroke4: newPrimary[950],
  approveStroke: green[500],
  approveStroke2: green[700],
  approveStroke3: green[900],
  criticalStroke: red[500],
  criticalStroke2: red[800],
  criticalStroke3: red[950],
};

export const lightPalette: ThemeOptions['palette'] = {
  ...customColors,
  mode: 'light',
  background: {
    default: customColors.bg3,
    paper: customColors.bg,
  },
  text: {
    primary: customColors.text1,
    secondary: customColors.text2,
    disabled: customColors.text3,
  },
  primary: {
    light: customColors.primaryBrandBg2,
    main: customColors.primaryBrandBg,
    dark: customColors.primaryBrandBg,
  },
  secondary: {
    light: customColors.bg4,
    main: customColors.negativeBg,
    dark: customColors.negativeBg,
  },
  success: {
    light: customColors.approveBg2,
    main: customColors.approveBg,
    dark: customColors.approveBg,
  },
  error: {
    light: customColors.criticalBg2,
    main: customColors.criticalBg,
    dark: customColors.criticalBg,
  },
  warning: {
    light: customColors.warningBg,
    main: customColors.warningBg,
    dark: customColors.warningBg,
  },
  info: {
    light: customColors.primaryBrandBg2,
    main: customColors.primaryBrandBg,
    dark: customColors.primaryBrandBg,
  },
  common: {
    black: customColors.negativeBg,
    white: customColors.bg,
  },
  action: {
    hover: customColors.hover,
    active: customColors.press,
    hoverOpacity: 0.05,
  },
};
